import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/lib/scrollTo";
import { CircularBadge, StarDoodle, PlusDoodle } from "@/components/doodles";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.06em]">
    <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-24 sm:pt-28 pb-14 overflow-hidden">
      <StarDoodle className="absolute top-32 left-[5%] w-9 h-9 text-gold hidden md:block" />
      <PlusDoodle className="absolute top-64 right-[6%] w-6 h-6 text-pink hidden md:block" />
      <PlusDoodle className="absolute bottom-28 left-[10%] w-5 h-5 text-royal hidden md:block" />
      <div aria-hidden="true" className="absolute -bottom-4 left-0 w-full pointer-events-none select-none">
        <p className="font-display font-extrabold uppercase text-[17vw] leading-none text-stroke opacity-[0.07] whitespace-nowrap">
          evolve with rosa
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
        className="absolute top-28 right-[6%] w-28 h-28 sm:w-40 sm:h-40 text-ink z-20 hidden md:block"
        data-testid="hero-badge"
      >
        <CircularBadge
          text="★ BACK STRONGER THAN EVER ★ EVOLVE"
          className="w-full h-full"
          inner={
            <span className="bg-gold border-[3px] border-ink rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center shadow-[3px_3px_0_#0d0d0d]">
              <StarDoodle className="w-6 h-6 text-pink" />
            </span>
          }
        />
      </motion.div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 w-full relative z-10">
        <span className="inline-block bg-ink text-cream font-bebas tracking-[0.25em] text-xs sm:text-sm px-3 py-1.5 -rotate-2 mb-6">
          COACH • ATHLETE • BELIEVER
        </span>
        <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tight text-[clamp(2.5rem,6.5vw,6rem)]" data-testid="hero-headline">
          <Line delay={0.15}>START WHERE</Line>
          <Line delay={0.28}>
            <span className="text-stroke">YOU ARE</span>
            <span className="text-pink">.</span>
          </Line>
          <Line delay={0.41}>
            <span className="hl-pink">EVOLVE</span> INTO
          </Line>
          <Line delay={0.54}>
            WHO YOU'RE
            <br />
            MEANT TO BE<span className="text-pink">.</span>
          </Line>
        </h1>
        <p className="font-hand text-2xl sm:text-3xl text-pink mt-6 -rotate-1">
          you don't have to know where to start — that's what I'm here for
        </p>
        <p className="mt-4 max-w-xl text-base sm:text-lg text-ink/70 font-semibold">
          Online fitness coaching for beginners, comeback stories, and competitive athletes.
          Faith-fueled, beginner-friendly, zero "gym bro" energy — just a plan, a coach who's lived it,
          and a whole lot of encouragement.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <button onClick={() => scrollToId("contact")} data-testid="hero-cta-primary" className="btn-pop bg-pink text-cream px-7 py-4 text-base sm:text-lg">
            Start Your Journey <ArrowRight className="w-5 h-5" />
          </button>
          <button onClick={() => scrollToId("story")} data-testid="hero-cta-secondary" className="btn-pop bg-white text-ink px-7 py-4 text-base sm:text-lg">
            Read My Comeback
          </button>
        </div>
        <p className="mt-8 font-bebas tracking-[0.2em] text-sm text-ink/60 uppercase">
          8 yrs gymnastics ★ NASM-certified ★ CF teen semifinalist '24
        </p>
      </div>

      <button
        onClick={() => scrollToId("about")}
        data-testid="hero-scroll-cue"
        className="absolute bottom-5 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1 font-hand text-xl text-ink/60 hover:text-pink transition-colors"
      >
        the good stuff is below
        <span className="animate-bounce text-pink text-2xl leading-none">↓</span>
      </button>
    </section>
  );
};
